import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowsClockwise, CaretDown, CaretRight, CheckCircle, Copy, Lightning, Plus, UploadSimple, Users,
  WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import CreateDeepLinkDialog from "./rga/CreateDeepLinkDialog";

// Same tracker base the publisher portal advertises (TrackingLinksTab.js).
const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const TRACKER_DOMAIN = process.env.REACT_APP_TRACKER_DOMAIN || "";
const TRACKER_BASE = TRACKER_DOMAIN
  ? `${TRACKER_DOMAIN.replace(/\/$/, "")}/c`
  : `${BASE_URL}/api/click`;

const trackerUrl = (brandId, subId) => `${TRACKER_BASE}/${brandId}?subId1=${subId || "{subId1}"}`;
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-slate-100 text-slate-600",
  paused: "bg-amber-100 text-amber-700",
};

function Pill({ value }) {
  const v = (value || "").toLowerCase();
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATUS_STYLES[v] || "bg-slate-100 text-slate-600"}`}>
      {v || "—"}
    </span>
  );
}

function Step({ n, title, hint, children }) {
  return (
    <section className="border border-slate-200 rounded-md p-4 mb-6 bg-white">
      <div className="flex items-baseline gap-3 mb-1">
        <span className="text-[10px] font-bold text-red-700 tracking-[0.2em]">STEP {n}</span>
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      </div>
      {hint && <p className="text-xs text-slate-500 mb-3 max-w-3xl">{hint}</p>}
      {children}
    </section>
  );
}

function ago(iso) {
  if (!iso) return "never";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
}

function SourceBadge({ source }) {
  const api = source === "rga_api";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
      api ? "bg-indigo-50 text-indigo-700" : "bg-emerald-50 text-emerald-700"}`}>
      {api ? "API" : "Webhook"}
    </span>
  );
}

function ConversionsPanel({ conv, syncing, onSync }) {
  if (!conv) return <div className="text-xs text-slate-400">Loading…</div>;
  if (conv.error) return <div className="text-xs text-red-700">{conv.error}</div>;
  const run = conv.last_run;
  const failed = run?.status === "failed";
  const bySrc = conv.month?.by_source || {};
  const api = bySrc.rga_api || { n: 0, payout: 0 };
  const hook = bySrc.rga_postback || { n: 0, payout: 0 };
  return (
    <div data-testid="rga-conversions-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 text-xs">
          {!conv.configured ? (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <WarningCircle size={16} weight="fill" /> No API token for {conv.label}
            </span>
          ) : failed ? (
            <span className="inline-flex items-center gap-1 text-red-700" title={run.error_message}>
              <WarningCircle size={16} weight="fill" /> Last sync failed {ago(run.started_at)}: {run.error_message}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle size={16} weight="fill" />
              {run ? `Synced ${ago(run.started_at)}` : "Not synced yet"}
              {run?.counts && (
                <span className="text-slate-500">
                  · {run.counts.seen} checked · {run.counts.created} new
                </span>
              )}
            </span>
          )}
        </div>
        <Button size="sm" variant="outline" disabled={!conv.configured || syncing || conv.running} onClick={onSync}
          data-testid="rga-conversions-sync-btn">
          <ArrowsClockwise size={14} className={`mr-1 ${syncing ? "animate-spin" : ""}`} />
          {syncing || conv.running ? "Syncing…" : "Sync now"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          ["This month", `${conv.month?.conversions ?? 0}`, money(conv.month?.payout)],
          ["Via webhook", `${hook.n}`, money(hook.payout)],
          ["Filled by API", `${api.n}`, money(api.payout)],
          ["Since", (conv.month?.from || "").slice(0, 10), "UTC"],
        ].map(([label, big, small]) => (
          <div key={label} className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
            <div className="mt-0.5 flex items-baseline gap-2">
              <span className="text-lg font-semibold text-slate-900">{big}</span>
              <span className="text-xs text-slate-500">{small}</span>
            </div>
          </div>
        ))}
      </div>

      {conv.recent?.length ? (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Confirmed (UTC)</th><th className="px-3 py-2">Brand</th>
                <th className="px-3 py-2 text-right">Payout</th><th className="px-3 py-2 text-right">Publisher earns</th>
                <th className="px-3 py-2">Publisher</th><th className="px-3 py-2">Via</th>
              </tr>
            </thead>
            <tbody>
              {conv.recent.map((a) => (
                <tr key={a.action_id} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-mono whitespace-nowrap">{(a.event_date || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-1.5">{a.campaign_name || "—"}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{money(a.payout_usd)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-slate-600">{a.earning != null ? money(a.earning) : "—"}</td>
                  <td className="px-3 py-1.5 font-mono text-slate-600">{a.sub_id1 || "—"}</td>
                  <td className="px-3 py-1.5"><SourceBadge source={a.source} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-xs text-slate-400">No conversions yet for this account.</div>
      )}
    </div>
  );
}

function Tile({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="font-mono mt-1 text-sm">{children}</div>
    </div>
  );
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  } catch {
    toast.error("Copy failed");
  }
}

// Stored key is the portal avatar letter; this is just how the admin shows it.
// Display names shown to admins (white-labeled). Upstream RGA account owner
// for V is still "Vishy" — preserved in brands.rga_account_name / the row tooltip.
const ACCOUNT_NAMES = { A: "LD Ashok", V: "LD Kevin" };
const accountLabel = (a) => ACCOUNT_NAMES[a] || a || "—";

// Each RGA account is its own internal network (rga_vishy / rga_ashok); the
// page works on one at a time. The backend's /admin/rga/health `accounts`
// list overrides these labels when present.
const ACCOUNT_TABS = [
  { account: "V", label: "LD Kevin" },
  { account: "A", label: "LD Ashok" },
];

export default function AdminRga() {
  // Account tab (V = Vishy, A = Ashok). Sent as `account` on every
  // account-scoped call so lists, stats and uploads stay in that network.
  const [account, setAccount] = useState("V");
  const [health, setHealth] = useState(null);
  const [postbackInfo, setPostbackInfo] = useState(null);
  const [brands, setBrands] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Step 1 — placements (they arrive by the hourly feed; this adds one by hand)
  // Payout sheet (Brand | Geo | Payout) from RGA: preview → pick ambiguous → apply.
  const [payFile, setPayFile] = useState(null);
  const [payRows, setPayRows] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const [manual, setManual] = useState({ tracking_url: "", name: "" });

  // Step 2 — assignment
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState("");
  const [assign, setAssign] = useState({ publisher_id: "", payout_mode: "passthrough", pub_split_pct: "", pub_flat_amount: "" });
  const [assigning, setAssigning] = useState(false);
  const [deepLinkOpen, setDeepLinkOpen] = useState(false);
  // Step 3 — conversions, pulled from RGA's Conversions API
  const [conv, setConv] = useState(null);
  const [convSyncing, setConvSyncing] = useState(false);

  // Ignore responses from a tab the admin has already switched away from.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const [h, pb, b, p] = await Promise.all([
        API.get("/admin/rga/health", { params: { account } }),
        API.get("/admin/rga/postback-info"),
        API.get("/admin/rga/brands", { params: { account } }),
        API.get("/admin/publishers"),
      ]);
      if (seq !== loadSeq.current) return;
      setHealth(h.data);
      setPostbackInfo(pb.data);
      setBrands(b.data.items || []);
      const pubs = Array.isArray(p.data) ? p.data : (p.data?.items || []);
      setPublishers(pubs.filter((u) => (u.role || "publisher") === "publisher"));
    } catch (err) {
      if (seq !== loadSeq.current) return;
      toast.error(err.response?.data?.detail || "Failed to load LD");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [account]);

  useEffect(() => { load(); }, [load]);

  // Conversions panel: the tab's account only; a response from a tab the
  // admin already left is dropped.
  const convSeq = useRef(0);
  const loadConversions = useCallback(async () => {
    const seq = ++convSeq.current;
    try {
      const { data } = await API.get("/admin/rga/conversions", { params: { account } });
      if (seq === convSeq.current) setConv(data);
    } catch (err) {
      if (seq === convSeq.current) setConv({ error: err.response?.data?.detail || "Could not load conversions" });
    }
  }, [account]);

  useEffect(() => { loadConversions(); }, [loadConversions]);

  // Sync now starts a background run (RGA's API can take ~40s a page); the
  // panel then polls until it finishes and reports that run.
  const syncConversions = async () => {
    setConvSyncing(true);
    const acc = account;
    const before = conv?.last_run?.started_at || "";
    try {
      const { data } = await API.post("/admin/rga/conversions/sync", null, { params: { account: acc } });
      if (data.status === "already_running") toast.message("A sync for this account is already running");
      for (let i = 0; i < 90; i += 1) {   // up to ~6 minutes
        await new Promise((r) => setTimeout(r, 4000));
        const { data: st } = await API.get("/admin/rga/conversions", { params: { account: acc } });
        if (acc === account) setConv(st);
        const run = st.last_run;
        if (!st.running && run && run.started_at !== before) {
          if (run.status === "failed") toast.error(`Conversions sync failed: ${run.error_message}`);
          else toast.success(`${st.label}: ${run.counts?.seen ?? 0} checked · ${run.counts?.created ?? 0} new`
            + (run.counts?.superseded ? ` · ${run.counts.superseded} CSV estimate(s) replaced` : ""));
          load();
          return;
        }
      }
      toast.message("Still syncing — the panel will show the result when it finishes");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Conversions sync failed");
    } finally {
      setConvSyncing(false);
    }
  };

  // Switching account: drop the other account's rows, picks and previews so
  // nothing from one tab is applied while the other is showing.
  const switchAccount = (acc) => {
    if (acc === account) return;
    setAccount(acc);
    setBrands([]);
    setHealth(null);
    setSelected(new Set());
    setExpanded(new Set());
    setPayFile(null);
    setPayRows(null);
    setConv(null);
  };

  // Brands still on the pre-split `rga` network appear on neither tab.
  const [migrating, setMigrating] = useState(false);
  const rerunMigration = async () => {
    setMigrating(true);
    try {
      const { data } = await API.post("/admin/rga/migrate");
      if (data.legacy_brand_count) {
        toast.error(`${data.legacy_brand_count} brands are still on the old RGA network`);
      } else {
        toast.success(`Moved ${data.brands || 0} brands to their RGA account`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "RGA migration failed");
    } finally {
      setMigrating(false);
    }
  };
  const accountTabs = (health?.accounts?.length
    ? health.accounts.map((a) => ({ account: a.account, label: a.label || accountLabel(a.account) }))
    : ACCOUNT_TABS);

  // Every row is this tab's account, so only the text search filters.
  const visibleBrands = useMemo(() => {
    const q = search.trim().toLowerCase();
    return brands.filter((b) =>
      !q || [b.name, b.advertiser_name, b.campaign_id, b.rga_placement_id]
        .some((v) => String(v || "").toLowerCase().includes(q)));
  }, [brands, search]);

  const previewPayouts = async () => {
    if (!payFile) return;
    setPayBusy(true);
    try {
      const form = new FormData();
      form.append("file", payFile);
      const { data } = await API.post("/admin/rga/payouts/preview", form, { params: { account } });
      // Rows that matched one brand and have a rate are ticked by default.
      setPayRows(data.rows.map((r) => ({ ...r, include: Boolean(r.campaign_id && r.max_pct) })));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read the payout sheet");
    } finally {
      setPayBusy(false);
    }
  };

  const setPayRow = (line, patch) =>
    setPayRows((rows) => rows.map((r) => (r.line === line ? { ...r, ...patch } : r)));

  const payReady = (payRows || []).filter((r) => r.include && r.campaign_id && r.max_pct);

  const applyPayouts = async () => {
    if (!payReady.length) return;
    setPayBusy(true);
    try {
      const { data } = await API.post("/admin/rga/payouts/apply", {
        filename: payFile?.name || "",
        items: payReady.map((r) => ({
          campaign_id: r.campaign_id, payout_display: r.payout_display,
          min_pct: r.min_pct, max_pct: r.max_pct, terms: r.terms, geos: r.geos,
        })),
      });
      toast.success(`Payouts set on ${data.applied.length} brand(s) · geos filled on ${data.geo_filled.length} · ${data.assignments_updated} assignment(s) updated`);
      if (data.errors.length) toast.error(`${data.errors.length} row(s) failed: ${data.errors.map((e) => e.error).join("; ")}`);
      setPayRows(null);
      setPayFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Applying payouts failed");
    } finally {
      setPayBusy(false);
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.post("/admin/rga/brands", manual, { params: { account } });
      toast.success(`${data.created ? "Created" : "Updated"} brand ${data.brand?.campaign_id}`);
      setManual({ tracking_url: "", name: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Add failed");
    }
  };

  // ── Step 2 ────────────────────────────────────────────────────────
  const toggle = (setFn, id) => setFn((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Brands the chosen publisher already runs (active/paused) can't be picked
  // again; soft-unassigned ("inactive") rows count as free.
  const isAssignedToChosen = useCallback((b) => !!assign.publisher_id
    && b.assignments.some((a) => a.publisher_id === assign.publisher_id && a.status !== "inactive"),
  [assign.publisher_id]);

  const selectableBrands = useMemo(
    () => visibleBrands.filter((b) => !isAssignedToChosen(b)), [visibleBrands, isAssignedToChosen]);

  const allVisibleSelected = selectableBrands.length > 0 && selectableBrands.every((b) => selected.has(b.campaign_id));
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    selectableBrands.forEach((b) => (allVisibleSelected ? next.delete(b.campaign_id) : next.add(b.campaign_id)));
    return next;
  });

  // Drop ticks that no longer apply: brands that left the list, or that the
  // newly chosen publisher already has.
  useEffect(() => {
    const byId = new Map(brands.map((b) => [b.campaign_id, b]));
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => byId.has(id) && !isAssignedToChosen(byId.get(id))));
      return next.size === prev.size ? prev : next;
    });
  }, [brands, isAssignedToChosen]);

  const [unassigning, setUnassigning] = useState(new Set());
  const unassign = async (b, a) => {
    const key = `${b.campaign_id}:${a.publisher_id}`;
    if (unassigning.has(key)) return;
    if (!window.confirm(`Unassign ${b.name} from ${a.publisher_name}? The assignment is deactivated (kept on file) and the publisher stops getting this brand.`)) return;
    setUnassigning((prev) => new Set(prev).add(key));
    try {
      const { data } = await API.post("/admin/coverage/bulk-unassign",
        { publisher_id: a.publisher_id, brand_ids: [b.campaign_id] });
      toast.success(data.removed ? "Unassigned" : "Already unassigned");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unassign failed");
    } finally {
      setUnassigning((prev) => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  const doAssign = async () => {
    const brandIds = [...selected];
    // bulk-assign treats a missing/empty brand_ids as "every brand in the
    // catalog" — never send it without an explicit list.
    if (brandIds.length === 0 || !assign.publisher_id) return;
    const body = {
      publisher_id: assign.publisher_id,
      brand_ids: brandIds,
      payout_mode: assign.payout_mode,
    };
    if (assign.pub_split_pct !== "") body.pub_split_pct = Number(assign.pub_split_pct);
    if (assign.payout_mode === "flat_cpa") body.pub_flat_amount = Number(assign.pub_flat_amount);
    setAssigning(true);
    try {
      const { data } = await API.post("/admin/publisher-brands/bulk-assign", body);
      toast.success(`Assigned ${data.assigned} · skipped ${data.skipped}`);
      (data.skip_details || []).slice(0, 5).forEach((s) => toast.message(`${s.brand_name || s.brand_id}: ${s.reason}`));
      setSelected(new Set());
      setExpanded(new Set(brandIds));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Assign failed");
    } finally {
      setAssigning(false);
    }
  };

  const assignDisabled = assigning || selected.size === 0 || !assign.publisher_id
    || (assign.payout_mode === "flat_cpa" && !(Number(assign.pub_flat_amount) > 0));

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-rga-page">
      <div className="mb-6">
        <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-red-700">Placements → publishers → webhooks</p>
        <h1 className="font-heading text-2xl font-semibold text-slate-900">LD (RGA)</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Each RGA placement is one brand. Placements arrive from each account's feed, you assign them to
          publishers, and conversions come in by webhook and from RGA's Conversions API — all automatic.
        </p>
      </div>

      <div className="flex items-center gap-2 border-b border-slate-200 mb-6" role="tablist" data-testid="rga-account-tabs">
        {accountTabs.map((t) => (
          <button key={t.account} type="button" role="tab" aria-selected={account === t.account}
            onClick={() => switchAccount(t.account)} data-testid={`rga-account-tab-${t.account}`}
            className={`pb-3 px-4 font-semibold text-sm transition-colors border-b-2 ${
              account === t.account
                ? "border-red-700 text-red-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {health?.legacy_brand_count > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="rga-legacy-warning">
          <span>
            {health.legacy_brand_count} RGA brand{health.legacy_brand_count === 1 ? " is" : "s are"} still on the old
            single RGA network, so {health.legacy_brand_count === 1 ? "it shows" : "they show"} on neither tab. The move to
            LD Kevin / LD Ashok normally runs when the server starts.
          </span>
          <Button size="sm" variant="outline" disabled={migrating} onClick={rerunMigration}
            data-testid="rga-rerun-migration-btn">
            {migrating ? "Moving…" : "Move them now"}
          </Button>
        </div>
      )}

      <div className="border border-slate-200 rounded-md p-4 mb-6 bg-white grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile label="Brands">{health?.brand_count ?? "—"}</Tile>
        <Tile label="Publisher assignments">{health?.assignment_count ?? "—"}</Tile>
        <Tile label="Conversions (all time)">{health?.action_count ?? "—"}</Tile>
        <Tile label="Conversions synced">
          {conv?.last_run?.started_at
            ? `${conv.last_run.started_at.slice(0, 16).replace("T", " ")} UTC`
            : "never"}
        </Tile>
      </div>

      <Step n={1} title="Placements & payouts"
        hint="New placements arrive on their own: each account's RGA feed is read every hour (or use Sync now on this account's card in Brands). Create a deep link to any merchant page, add an existing placement by hand, and set payouts from RGA's sheet — the feed carries no amounts.">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-100 bg-red-50/40 px-4 py-3">
          <div>
            <div className="text-sm font-medium text-slate-900">Deep link to a merchant page</div>
            <div className="text-xs text-slate-500">Creates the placement on RGA ({accountLabel(account)}) and adds it here as a brand.</div>
          </div>
          <Button className="bg-red-700 hover:bg-red-800 text-white" onClick={() => setDeepLinkOpen(true)}
            data-testid="rga-create-deeplink-btn">
            <Plus size={14} className="mr-1" /> Create deep link
          </Button>
        </div>
        <CreateDeepLinkDialog open={deepLinkOpen} onOpenChange={setDeepLinkOpen} account={account}
          accountLabel={accountLabel(account)} onCreated={load} />
        <form onSubmit={addManual} className="flex flex-wrap gap-3 items-end mt-4 pt-4 border-t border-slate-100">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-xs">Add one placement: tracking URL</Label>
            <Input value={manual.tracking_url} placeholder="https://go.news247info.com/<placement-uuid>"
              onChange={(e) => setManual({ ...manual, tracking_url: e.target.value })} className="h-9" />
          </div>
          <div className="min-w-[200px]">
            <Label className="text-xs">Brand name</Label>
            <Input value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} className="h-9" />
          </div>
          <Button type="submit" variant="outline" disabled={!manual.tracking_url.trim()}>
            <Plus size={14} className="mr-1" /> Add brand
          </Button>
        </form>

        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="rga-payout-sheet">
          <Label className="text-xs">Set payouts from RGA's sheet (Brand · Geo · Payout, .xlsx or .csv)</Label>
          <p className="text-[11px] text-slate-500 mb-2">
            Rows are matched to placements by name. Publishers see the % range; every tier line is kept.
            Geos only fill brands that have none. Saved payouts are pinned, so syncs and imports keep them.
          </p>
          <div className="flex flex-wrap gap-3 items-center">
            <Input key={`pay-${account}`} type="file" accept=".xlsx,.csv" className="h-9 max-w-xs"
              onChange={(e) => { setPayFile(e.target.files?.[0] || null); setPayRows(null); }} />
            <Button variant="outline" disabled={!payFile || payBusy} onClick={previewPayouts}>
              <UploadSimple size={14} className="mr-1" /> Preview
            </Button>
            <Button className="bg-red-700 hover:bg-red-800 text-white"
              disabled={!payReady.length || payBusy} onClick={applyPayouts}>
              Set payouts on {payReady.length} brand(s)
            </Button>
          </div>

          {payRows && (
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-1.5">Use</th><th className="px-2 py-1.5">Sheet brand</th>
                    <th className="px-2 py-1.5">Placement</th><th className="px-2 py-1.5">Payout</th>
                    <th className="px-2 py-1.5">Tiers</th><th className="px-2 py-1.5">Geos</th>
                  </tr>
                </thead>
                <tbody>
                  {payRows.map((r) => (
                    <tr key={r.line} className="border-t border-slate-100 align-top">
                      <td className="px-2 py-1.5">
                        <input type="checkbox" checked={r.include} disabled={!r.campaign_id || !r.max_pct}
                          onChange={(e) => setPayRow(r.line, { include: e.target.checked })} />
                      </td>
                      <td className="px-2 py-1.5 font-semibold">{r.brand}</td>
                      <td className="px-2 py-1.5 min-w-[220px]">
                        {r.candidates.length > 1 || (!r.campaign_id && r.candidates.length) ? (
                          <Select value={r.campaign_id || ""}
                            onValueChange={(v) => setPayRow(r.line, { campaign_id: v, include: Boolean(r.max_pct) })}>
                            <SelectTrigger className="h-8 bg-white"><SelectValue placeholder="Pick the placement" /></SelectTrigger>
                            <SelectContent>
                              {r.candidates.map((c) => (
                                <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : r.campaign_id ? (
                          <span>{r.candidates[0]?.name || r.campaign_id}</span>
                        ) : (
                          <span className="text-amber-700">{r.problem}</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 font-semibold whitespace-nowrap">
                        {r.payout_display || <span className="text-amber-700">{r.problem}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-slate-600 max-w-[360px]">{r.terms}</td>
                      <td className="px-2 py-1.5 text-slate-600 max-w-[200px]" title={r.geos.join(", ")}>
                        {r.geos.length} {r.geos.length === 1 ? "country" : "countries"}
                        {r.unknown_geos.length > 0 && (
                          <div className="text-amber-700">unknown: {r.unknown_geos.join(", ")}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Step>

      <Step n={2} title="Assign brands to publishers"
        hint="Tick brands, pick a publisher and payout, then assign. Passthrough pays the publisher their split of RGA's payout. Expand a brand to see who runs it, their tracking link, and last-30-day clicks, RGA-validated clicks and conversions.">
        <div className="flex flex-wrap gap-3 items-end mb-4 p-3 rounded bg-slate-50 border border-slate-200">
          <div className="min-w-[240px]">
            <Label className="text-xs">Publisher</Label>
            <Select value={assign.publisher_id} onValueChange={(v) => setAssign({ ...assign, publisher_id: v })}>
              <SelectTrigger className="h-9 bg-white"><SelectValue placeholder="Choose publisher" /></SelectTrigger>
              <SelectContent>
                {publishers.map((p) => {
                  const id = String(p.id || p._id || "");
                  if (!id) return null;
                  const label = p.company_name || p.name || p.email || id;
                  return (
                    <SelectItem key={id} value={id}>
                      {label + (p.sub_id ? ` · ${p.sub_id}` : "")}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Label className="text-xs">Payout</Label>
            <Select value={assign.payout_mode} onValueChange={(v) => setAssign({ ...assign, payout_mode: v })}>
              <SelectTrigger className="h-9 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="passthrough">Passthrough (split)</SelectItem>
                <SelectItem value="flat_cpa">Flat CPA</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-28">
            <Label className="text-xs">Pub split %</Label>
            <Input type="number" min="0" max="100" placeholder="default" className="h-9 bg-white"
              value={assign.pub_split_pct} onChange={(e) => setAssign({ ...assign, pub_split_pct: e.target.value })} />
          </div>
          {assign.payout_mode === "flat_cpa" && (
            <div className="w-28">
              <Label className="text-xs">Flat $</Label>
              <Input type="number" min="0" step="0.01" className="h-9 bg-white"
                value={assign.pub_flat_amount} onChange={(e) => setAssign({ ...assign, pub_flat_amount: e.target.value })} />
            </div>
          )}
          <Button className="bg-red-700 hover:bg-red-800 text-white" disabled={assignDisabled} onClick={doAssign}>
            <Users size={14} className="mr-1" />
            {assigning ? "Assigning…" : `Assign ${selected.size} brand${selected.size === 1 ? "" : "s"}`}
          </Button>
        </div>

        <div className="flex gap-2 mb-3">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search brands"
            className="h-9 max-w-xs" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label="Select all" />
                </th>
                <th className="px-3 py-2">Brand</th>
                <th className="px-3 py-2">Placement</th>
                <th className="px-3 py-2">Account</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Publishers</th>
                <th className="px-3 py-2 text-right">Clicks</th>
                <th className="px-3 py-2 text-right">RGA valid</th>
                <th className="px-3 py-2 text-right">Conv.</th>
                <th className="px-3 py-2 text-right">Payout</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : visibleBrands.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No brands yet — upload placements in step 1.</td></tr>
              ) : visibleBrands.map((b) => {
                const open = expanded.has(b.campaign_id);
                return (
                  <Fragment key={b.campaign_id}>
                    <tr className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        {isAssignedToChosen(b) ? (
                          <span className="text-[10px] text-emerald-700 font-medium" title="Already assigned to the chosen publisher">✓</span>
                        ) : (
                          <input type="checkbox" checked={selected.has(b.campaign_id)}
                            onChange={() => toggle(setSelected, b.campaign_id)} aria-label={`Select ${b.name}`} />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <button type="button" className="flex items-center gap-1 text-left"
                          onClick={() => toggle(setExpanded, b.campaign_id)}>
                          {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
                          <span>{b.name}</span>
                        </button>
                        <div className="text-[11px] text-slate-400 font-mono ml-4">{b.campaign_id}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-mono font-medium truncate max-w-[140px]" title={b.rga_placement_id}>
                          {/^\d+$/.test(b.rga_placement_id) ? `#${b.rga_placement_id}` : (b.rga_placement_id || "—")}
                        </div>
                        <div className="text-[11px] font-semibold text-slate-500">{b.rga_placement_type || "Placement"}</div>
                      </td>
                      <td className="px-3 py-2 text-xs font-semibold" title={b.rga_account_name ? `Upstream RGA account: ${b.rga_account_name}` : ""}>{accountLabel(b.rga_account)}</td>
                      <td className="px-3 py-2"><Pill value={b.status} /></td>
                      <td className="px-3 py-2 text-xs">{b.assignments.filter((a) => a.status !== "inactive").length}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{b.totals.clicks}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{b.totals.valid}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{b.totals.conversions}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {b.totals.payout > 0 ? money(b.totals.payout) : ((b.payout_display || "—").replace(/[\s\–\—\-]+$/, "").trim() || "—")}
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-slate-50/60">
                        <td />
                        <td colSpan={8} className="px-3 py-3">
                          <div className="mb-3 p-3 bg-white border border-slate-200 rounded-md flex flex-wrap gap-4 items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-700">
                                Placement {/^\d+$/.test(b.rga_placement_id) ? `#${b.rga_placement_id}` : b.rga_placement_id}
                              </span>
                              {b.rga_placement_uuid && b.rga_placement_uuid !== b.rga_placement_id && (
                                <button type="button" onClick={() => copyText(b.rga_placement_uuid)}
                                        className="font-mono text-[11px] text-slate-500 bg-slate-100 hover:bg-slate-200 px-1.5 py-0.5 rounded border border-slate-200 transition-colors flex items-center gap-1"
                                        title="Click to copy original Placement UUID">
                                  <Copy size={11} /> UUID: {b.rga_placement_uuid}
                                </button>
                              )}
                              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                                (b.rga_placement_type || "").toLowerCase().includes("deep")
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-blue-100 text-blue-700"
                              }`}>
                                {b.rga_placement_type || "Direct Link"}
                              </span>
                            </div>

                            <div className="w-full flex items-center gap-2" data-testid={`rga-payout-${b.campaign_id}`}>
                              <span className="font-semibold text-slate-700 whitespace-nowrap">Payout</span>
                              <Input
                                defaultValue={b.payout_display || ""}
                                placeholder="e.g. $22 – $350 or 5%  (empty = use imported payout)"
                                className="h-8 text-xs"
                                onBlur={async (e) => {
                                  const v = e.target.value.trim();
                                  if (v === (b.payout_display || "")) return;
                                  try {
                                    await API.put(`/admin/rga/brands/${encodeURIComponent(b.campaign_id)}/payout`, { payout: v });
                                    toast.success(v ? "Payout saved" : "Payout override cleared");
                                    load();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || "Save failed");
                                  }
                                }}
                              />
                            </div>
                            <div className="w-full flex items-center gap-2" data-testid={`rga-deeplink-${b.campaign_id}`}>
                              <span className="font-semibold text-slate-700 whitespace-nowrap">Deeplink template</span>
                              <Input
                                defaultValue={b.deeplink_template || ""}
                                placeholder="https://go.news247info.com/…?dest=[url]  (empty = no deep links)"
                                className="h-8 text-xs font-mono"
                                onBlur={async (e) => {
                                  const v = e.target.value.trim();
                                  if (v === (b.deeplink_template || "")) return;
                                  try {
                                    await API.put(`/admin/rga/brands/${encodeURIComponent(b.campaign_id)}/deeplink-template`, { deeplink_template: v });
                                    toast.success(v ? "Deeplink template saved" : "Deeplink template cleared");
                                    load();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || "Save failed");
                                  }
                                }}
                              />
                            </div>

                            {b.rga_destination_url && (
                              <div className="flex items-center gap-1.5 max-w-lg truncate">
                                <span className="text-slate-400 font-medium shrink-0">Destination URL:</span>
                                <a href={b.rga_destination_url} target="_blank" rel="noopener noreferrer" 
                                   className="text-blue-600 hover:underline truncate font-mono text-[11px]">
                                  {b.rga_destination_url}
                                </a>
                                <button type="button" onClick={() => copyText(b.rga_destination_url)} className="text-slate-400 hover:text-slate-600 shrink-0">
                                  <Copy size={12} />
                                </button>
                              </div>
                            )}

                            {b.tracking_link && (
                              <div className="flex items-center gap-1.5">
                                <span className="text-slate-400 font-medium">Direct Tracking Link:</span>
                                <button type="button" onClick={() => copyText(b.tracking_link)} className="font-mono text-[11px] text-slate-600 hover:text-slate-900 flex items-center gap-1">
                                  <Copy size={12} /> {b.tracking_link}
                                </button>
                              </div>
                            )}
                          </div>

                          {Array.isArray(b.rga_keywords) && b.rga_keywords.length > 0 && (
                            <div className="mb-3 p-3 bg-white border border-slate-200 rounded-md">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-slate-700">Suggested Keywords ({b.rga_keywords.length})</span>
                                <button type="button" onClick={() => copyText(b.rga_keywords.join("; "))} className="text-[11px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                                  <Copy size={12} /> Copy All Keywords
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
                                {b.rga_keywords.map((kw, i) => (
                                  <span key={i} onClick={() => copyText(kw)} title="Click to copy keyword"
                                        className="cursor-pointer text-[11px] bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-0.5 rounded border border-slate-200 transition-colors">
                                    {kw}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {b.assignments.length === 0 ? (
                            <p className="text-xs text-slate-400 italic">Not assigned to any publisher yet.</p>
                          ) : (
                            <table className="w-full text-xs">
                              <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                                <tr>
                                  <th className="py-1 pr-3">Publisher</th><th className="py-1 pr-3">Status</th>
                                  <th className="py-1 pr-3">Payout</th><th className="py-1 pr-3">Tracking link</th>
                                  <th className="py-1 pr-3 text-right">Clicks</th><th className="py-1 pr-3 text-right">Valid</th>
                                  <th className="py-1 pr-3 text-right">Conv.</th><th className="py-1 pr-3 text-right">Pub earning</th><th className="py-1" />
                                </tr>
                              </thead>
                              <tbody>
                                {b.assignments.map((a) => {
                                  const url = trackerUrl(b.campaign_id, a.sub_id);
                                  return (
                                    <tr key={a.publisher_id} className="border-t border-slate-200/70">
                                      <td className="py-1.5 pr-3">{a.publisher_name}<div className="text-slate-400 font-mono">{a.sub_id}</div></td>
                                      <td className="py-1.5 pr-3"><Pill value={a.status} /></td>
                                      <td className="py-1.5 pr-3">
                                        {a.payout_mode === "flat_cpa" ? money(a.pub_flat_amount) : `${a.pub_split_pct ?? "—"}% split`}
                                      </td>
                                      <td className="py-1.5 pr-3">
                                        <button type="button" onClick={() => copyText(url)}
                                          className="flex items-center gap-1 font-mono text-[11px] text-slate-600 hover:text-slate-900 break-all text-left">
                                          <Copy size={12} className="shrink-0" /> {url}
                                        </button>
                                      </td>
                                      <td className="py-1.5 pr-3 text-right font-mono">{a.clicks}</td>
                                      <td className="py-1.5 pr-3 text-right font-mono">{a.valid}</td>
                                      <td className="py-1.5 pr-3 text-right font-mono">{a.conversions}</td>
                                      <td className="py-1.5 pr-3 text-right font-mono">{money(a.earning)}</td>
                                      <td className="py-1.5 text-right">
                                        {a.status !== "inactive" && (
                                          <button type="button" onClick={() => unassign(b, a)}
                                            disabled={unassigning.has(`${b.campaign_id}:${a.publisher_id}`)}
                                            className="text-[11px] text-red-700 hover:underline disabled:opacity-50">Unassign</button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                          {b.totals.clicks > b.assignments.reduce((n, a) => n + a.clicks, 0) && (
                            <p className="text-[11px] text-amber-700 mt-2">
                              Some clicks on this brand could not be matched to an assigned publisher (bad or missing subId1).
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Step>

      <Step n={3} title="Conversions"
        hint="Pulled automatically from RGA's Conversions API every hour, with the exact payout RGA paid on each. Conversions the webhook already recorded are skipped by transaction id, and any it missed are filled in — no CSV needed.">
        <ConversionsPanel conv={conv} syncing={convSyncing} onSync={syncConversions} />
      </Step>

      <Step n={4} title="Connect the webhooks (once)"
        hint="This is how RGA updates reach us. In the RGA portal → Webhooks, add one webhook per event type with the URL below. When the form lists the {placeholders}, map each to the internal value shown. Valid Click marks our click as accepted by RGA; Confirmed Click creates the conversion and pays the publisher.">
        {(postbackInfo?.webhooks || []).map((w) => (
          <div key={w.event_type} className="mb-3">
            <div className="flex items-center justify-between gap-3 mb-1">
              <span className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <Lightning size={14} /> {w.event_type}
              </span>
              <Button size="sm" variant="outline" onClick={() => copyText(w.url_template)}>
                <Copy size={14} className="mr-1" /> Copy
              </Button>
            </div>
            <code className="block text-[11px] font-mono break-all text-slate-700 bg-slate-50 border border-slate-200 rounded p-2">
              {w.url_template}
            </code>
          </div>
        ))}
        <p className="text-xs text-slate-500">
          Map: {Object.entries(postbackInfo?.macro_mapping || {}).map(([k, v]) => `${k} → ${v}`).join(" · ")}.
          {" "}We send sub1 = our click id, sub2 = publisher subId1, sub5 = placement id on every click.
        </p>
      </Step>
    </div>
  );
}
